import * as ENGINE from '@gnsx/genesys.js';

import type { PropertyMetadata } from '@gnsx/genesys.js';

import { ASSET_PATH_TYPES, normaliseAssetPath } from '@/editor/core/assetPathWarnings';
import { getUnifiedPropertyMetadata } from '@/editor/core/editorClassMeta';
import { readByPath } from '@/editor/core/propertyPathUtils';
import { editorStore } from '@/editor/store';

export type ActorQueryTransform = {
  position: string;
  rotation: string;
  scale: string;
};

export type ActorQueryReference = {
  kind: 'asset' | 'prefab';
  path: string;
  propertyPath: string;
  targetKind: 'actor' | 'component';
  actorId: string;
  componentId?: string;
  className?: string;
};

export type ActorQueryEditableProperty = {
  targetKind: 'actor' | 'component';
  actorId: string;
  componentId?: string;
  className: string;
  path: string;
  type: string;
  readonly: boolean;
  category?: string;
  value: unknown;
};

const EXCLUDED_PROPERTY_KEYS = new Set([
  'uuid',
  'editorData',
  'rootComponent',
]);

function resolveClassName(target: ENGINE.Actor | ENGINE.SceneComponent): string {
  return ENGINE.ClassRegistry.getClassName(target)
    ?? ENGINE.ClassRegistry.getClassOrConstructorName(target)
    ?? target.constructor.name;
}

function shouldSkipEditableProperty(
  propertyName: string,
  metadata: PropertyMetadata | undefined,
  value: unknown,
): boolean {
  if (EXCLUDED_PROPERTY_KEYS.has(propertyName)) return true;
  if (typeof value === 'function') return true;
  if (metadata?.hidden) return true;
  if (metadata?.editorOnly) return true;
  return false;
}

function collectReferencesForValue(
  value: unknown,
  meta: PropertyMetadata | undefined,
  propertyPath: string,
  references: Map<string, ActorQueryReference>,
  context: {
    targetKind: 'actor' | 'component';
    actorId: string;
    componentId?: string;
    className: string;
  },
): void {
  if (!meta) return;

  const type = meta.type;
  if (type && ASSET_PATH_TYPES.has(type)) {
    if (typeof value === 'string') {
      const path = normaliseAssetPath(value);
      if (!path) return;
      const dedupeKey = `${context.actorId}|${context.componentId ?? ''}|${propertyPath}|${path}`;
      references.set(dedupeKey, {
        kind: 'asset',
        path,
        propertyPath,
        targetKind: context.targetKind,
        actorId: context.actorId,
        componentId: context.componentId,
        className: context.className,
      });
    }
    return;
  }

  if (meta.type === 'object' && 'fields' in meta && meta.fields && value && typeof value === 'object') {
    for (const [fieldName, fieldMeta] of Object.entries(meta.fields)) {
      collectReferencesForValue(
        readByPath(value, fieldName),
        fieldMeta,
        `${propertyPath}.${fieldName}`,
        references,
        context,
      );
    }
    return;
  }

  if (meta.type === 'array' && 'child' in meta && meta.child && Array.isArray(value)) {
    value.forEach((item, index) => {
      collectReferencesForValue(
        item,
        meta.child,
        `${propertyPath}[${index}]`,
        references,
        context,
      );
    });
  }
}

function collectEditablePropertiesForValue(
  value: unknown,
  meta: PropertyMetadata | undefined,
  propertyPath: string,
  properties: ActorQueryEditableProperty[],
  context: {
    targetKind: 'actor' | 'component';
    actorId: string;
    componentId?: string;
    className: string;
  },
  sanitize: (value: unknown) => unknown,
): void {
  if (!meta) return;

  if (meta.type === 'object' && 'fields' in meta && meta.fields && value && typeof value === 'object') {
    for (const [fieldName, fieldMeta] of Object.entries(meta.fields)) {
      if (shouldSkipEditableProperty(fieldName, fieldMeta, readByPath(value, fieldName))) {
        continue;
      }
      collectEditablePropertiesForValue(
        readByPath(value, fieldName),
        fieldMeta,
        `${propertyPath}.${fieldName}`,
        properties,
        context,
        sanitize,
      );
    }
    return;
  }

  if (meta.type === 'array' && 'child' in meta && meta.child && Array.isArray(value)) {
    value.forEach((item, index) => {
      collectEditablePropertiesForValue(
        item,
        meta.child,
        `${propertyPath}[${index}]`,
        properties,
        context,
        sanitize,
      );
    });
    return;
  }

  const rootPropertyName = propertyPath.split('.')[0]?.replace(/\[\d+\]/, '') ?? propertyPath;
  if (shouldSkipEditableProperty(rootPropertyName, meta, value)) {
    return;
  }

  properties.push({
    targetKind: context.targetKind,
    actorId: context.actorId,
    componentId: context.componentId,
    className: context.className,
    path: propertyPath,
    type: meta.type ?? 'unknown',
    readonly: meta.readonly ?? false,
    category: meta.category,
    value: sanitize(value),
  });
}

function collectTargetReferences(
  target: ENGINE.Actor | ENGINE.SceneComponent,
  references: Map<string, ActorQueryReference>,
  context: {
    targetKind: 'actor' | 'component';
    actorId: string;
    componentId?: string;
    className: string;
  },
): void {
  const metadata = getUnifiedPropertyMetadata(target);
  for (const [propertyPath, propertyMeta] of Object.entries(metadata)) {
    const value = readByPath(target, propertyPath);
    collectReferencesForValue(value, propertyMeta, propertyPath, references, context);
  }
}

function collectTargetEditableProperties(
  target: ENGINE.Actor | ENGINE.SceneComponent,
  properties: ActorQueryEditableProperty[],
  context: {
    targetKind: 'actor' | 'component';
    actorId: string;
    componentId?: string;
    className: string;
  },
  sanitize: (value: unknown) => unknown,
): void {
  const metadata = getUnifiedPropertyMetadata(target);
  for (const [propertyName, propertyMeta] of Object.entries(metadata)) {
    const value = readByPath(target, propertyName);
    if (shouldSkipEditableProperty(propertyName, propertyMeta, value)) {
      continue;
    }
    collectEditablePropertiesForValue(value, propertyMeta, propertyName, properties, context, sanitize);
  }
}

export function serializeActorTransform(actor: ENGINE.Actor): ActorQueryTransform {
  return {
    position: ENGINE.DescriptionHelper.dumpPosition(actor.getWorldPosition()),
    rotation: ENGINE.DescriptionHelper.dumpRotation(actor.getWorldRotation()),
    scale: ENGINE.DescriptionHelper.dumpScale(actor.getWorldScale()),
  };
}

export function serializeActorBoundingBox(actor: ENGINE.Actor): ReturnType<typeof ENGINE.DescriptionHelper.dumpBoundingBox> {
  return ENGINE.DescriptionHelper.dumpBoundingBox(actor.calcBoundingBox());
}

export function serializeActorComponents(actor: ENGINE.Actor): unknown[] {
  const described = actor.describe({ includeComponentsDetails: true }) as { rootComponent?: unknown };
  if (!described.rootComponent) {
    return [];
  }
  return [described.rootComponent];
}

export function serializeActorChildIds(actorId: string): string[] {
  const outliner = editorStore.scene.outliner;
  const node = outliner.getNodeById(actorId);
  if (!node) {
    return [];
  }
  return node.children.map(child => child.id);
}

export function serializeActorReferences(actor: ENGINE.Actor): ActorQueryReference[] {
  const references = new Map<string, ActorQueryReference>();
  const actorId = actor.uuid;
  const actorClassName = resolveClassName(actor);

  collectTargetReferences(actor, references, {
    targetKind: 'actor',
    actorId,
    className: actorClassName,
  });

  const prefabPath = actor.getPrefabName();
  if (prefabPath) {
    const normalised = normaliseAssetPath(prefabPath);
    if (normalised) {
      references.set(`${actorId}|prefab|${normalised}`, {
        kind: 'prefab',
        path: normalised,
        propertyPath: '$prefab',
        targetKind: 'actor',
        actorId,
        className: actorClassName,
      });
    }
  }

  const components = actor.getComponents(ENGINE.SceneComponent);
  for (const component of components) {
    collectTargetReferences(component, references, {
      targetKind: 'component',
      actorId,
      componentId: component.uuid,
      className: resolveClassName(component),
    });
  }

  return Array.from(references.values()).sort((a, b) =>
    a.propertyPath.localeCompare(b.propertyPath) || a.path.localeCompare(b.path),
  );
}

export function serializeActorEditableProperties(
  actor: ENGINE.Actor,
  sanitize: (value: unknown) => unknown,
): ActorQueryEditableProperty[] {
  const properties: ActorQueryEditableProperty[] = [];
  const actorId = actor.uuid;

  collectTargetEditableProperties(actor, properties, {
    targetKind: 'actor',
    actorId,
    className: resolveClassName(actor),
  }, sanitize);

  const components = actor.getComponents(ENGINE.SceneComponent);
  for (const component of components) {
    collectTargetEditableProperties(component, properties, {
      targetKind: 'component',
      actorId,
      componentId: component.uuid,
      className: resolveClassName(component),
    }, sanitize);
  }

  return properties.sort((a, b) =>
    (a.componentId ?? '').localeCompare(b.componentId ?? '')
    || a.path.localeCompare(b.path),
  );
}

export function buildActorQueryFields(
  actor: ENGINE.Actor,
  sanitize: (value: unknown) => unknown,
): {
  transform: ActorQueryTransform;
  components: unknown[];
  childIds: string[];
  references: ActorQueryReference[];
  editableProperties: ActorQueryEditableProperty[];
} {
  return {
    transform: serializeActorTransform(actor),
    components: serializeActorComponents(actor),
    childIds: serializeActorChildIds(actor.uuid),
    references: serializeActorReferences(actor),
    editableProperties: serializeActorEditableProperties(actor, sanitize),
  };
}

export type ActorQueryShapeField =
  | 'describe'
  | 'transform'
  | 'boundingBox'
  | 'components'
  | 'childIds'
  | 'references'
  | 'editableProperties';

export type ActorQueryShapeOptions = {
  fields: ReadonlySet<ActorQueryShapeField>;
  includeComponentsDetails: boolean;
};

function shapeIncludes(shape: ActorQueryShapeOptions, field: ActorQueryShapeField): boolean {
  return shape.fields.has(field);
}

export function serializeActorForQuery(
  actor: ENGINE.Actor,
  actorId: string,
  shape: ActorQueryShapeOptions,
  sanitize: (value: unknown) => unknown,
  serializeDescribe: (value: unknown, options?: { includeComponentsDetails?: boolean }) => unknown,
): Record<string, unknown> {
  const result: Record<string, unknown> = { id: actorId };

  if (shapeIncludes(shape, 'describe')) {
    const described = sanitize(serializeDescribe(actor, {
      includeComponentsDetails: shape.includeComponentsDetails,
    }));
    if (described && typeof described === 'object') {
      Object.assign(result, described as Record<string, unknown>);
    }
  }

  if (shapeIncludes(shape, 'transform')) {
    result.transform = serializeActorTransform(actor);
  }

  if (shapeIncludes(shape, 'boundingBox')) {
    result.boundingBox = serializeActorBoundingBox(actor);
  }

  if (shapeIncludes(shape, 'components')) {
    result.components = sanitize(serializeActorComponents(actor));
  }

  if (shapeIncludes(shape, 'childIds')) {
    result.childIds = serializeActorChildIds(actorId);
  }

  if (shapeIncludes(shape, 'references')) {
    result.references = sanitize(serializeActorReferences(actor));
  }

  if (shapeIncludes(shape, 'editableProperties')) {
    result.editableProperties = sanitize(serializeActorEditableProperties(actor, sanitize));
  }

  return result;
}
